import React, { useState, useEffect, useCallback } from 'react';
import { Activity, AlertTriangle, ShieldAlert, Clock } from 'lucide-react';
import '../styles/Vol01Panel.css';

/**
 * VOL01 Variance-Harvest Gauge — VIEWER
 *
 * ACCESS: Admin only (Stage 1) | ENABLED_FOR: admin | TESTED: pending
 *
 * Pure viewer over the validated xau_range pipeline (VOL01_APP_PLAN.md). This component
 * COMPUTES NO STRATEGY MATH: every market value (iv, k0, edge, forecast) renders VERBATIM
 * as the string the pipeline published (D-067 byte parity). Numbers are parsed ONLY for
 * sparkline pixel geometry (display), never for values or decisions. The `state` field is
 * the sole authority — nothing here derives DEPLOY/FLAT from edge.
 */
const API = process.env.REACT_APP_API_URL;
const ASSETS = ['BTC', 'ETH'];
const REFRESH_MS = 5 * 60 * 1000;

async function api(path) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

function FreshBadge({ level }) {
  const cls = { FRESH: 'ok', WARN: 'warn', STALE: 'bad', OFFLINE: 'bad', UNKNOWN: 'warn' }[level] || 'warn';
  return <span className={`v1-badge ${cls}`}>{level || '—'}</span>;
}

function Spark({ rows }) {
  if (!rows || rows.length < 2) return null;
  // display-only geometry; values on screen always come from the verbatim strings
  const ys = rows.map((r) => Number(r.edge));
  const min = Math.min(...ys, 0);
  const max = Math.max(...ys, 0);
  const W = 260;
  const H = 48;
  const x = (i) => (i / (ys.length - 1)) * W;
  const y = (v) => H - ((v - min) / (max - min || 1)) * H;
  const pts = ys.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="v1-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <line x1="0" x2={W} y1={y(0)} y2={y(0)} className="v1-spark-zero" />
      <polyline points={pts} className="v1-spark-line" />
    </svg>
  );
}

function AssetCard({ asset, row, history }) {
  if (!row) {
    return (
      <div className="v1-card v1-unavailable">
        <div className="v1-card-head"><h3>{asset}</h3><span className="v1-state bad">NO DATA</span></div>
        <p className="v1-dim">No state received for this asset yet.</p>
      </div>
    );
  }
  const unavailable = row.state === 'UNAVAILABLE' || row.current_status === 'UNAVAILABLE';
  const stateCls = unavailable ? 'bad' : row.state === 'DEPLOY' ? 'ok' : 'flat';
  const stale = row.data_freshness === 'STALE' || row.data_freshness === 'UNKNOWN';
  return (
    <div className={`v1-card ${stale ? 'v1-stale' : ''}`}>
      <div className="v1-card-head">
        <h3>{asset}</h3>
        <FreshBadge level={row.data_freshness} />
        <span className={`v1-state ${stateCls}`}>
          {unavailable ? 'UNAVAILABLE' : row.state}
        </span>
      </div>
      {unavailable && (
        <p className="v1-error"><AlertTriangle size={14} /> {row.current_error || row.error || 'pipeline could not build this asset'}</p>
      )}
      {stale && !unavailable && (
        <p className="v1-dim"><Clock size={13} /> as of {row.date} — not current</p>
      )}
      <div className="v1-grid">
        <div><label>Edge (vp)</label><span className={`v1-num ${stateCls}`}>{row.edge ?? '—'}</span></div>
        <div><label>IV (prev close)</label><span className="v1-num">{row.iv ?? '—'}</span></div>
        <div><label>Strike net (K0)</label><span className="v1-num">{row.k0 ?? '—'}</span></div>
        <div><label>HAR forecast</label><span className="v1-num">{row.rv_fc_vol ?? '—'}</span></div>
        <div><label>Data date</label><span className="v1-num">{row.date ?? '—'}</span></div>
        <div><label>Stale days</label><span className="v1-num">{row.stale_days ?? '—'}</span></div>
      </div>
      {row.state === 'DEPLOY' && !unavailable && !stale && (
        <div className="v1-action">
          SELL ~30d {asset} strangle · hold to tenor · capital 0.50×K0 per unit vega
          <span className="v1-dim"> (sizing echoed from pipeline — confirm live quotes first)</span>
        </div>
      )}
      <Spark rows={history} />
    </div>
  );
}

export default function Vol01Panel() {
  const [state, setState] = useState(null);
  const [hist, setHist] = useState({});
  const [err, setErr] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await api('/api/vol01/state');
      setState(s);
      const h = {};
      for (const a of ASSETS) {
        try {
          h[a] = (await api(`/api/vol01/history?asset=${a}&days=120`)).rows || [];
        } catch { h[a] = []; }
      }
      setHist(h);
      setErr(null);
      setLoadedAt(new Date());
    } catch (e) {
      setErr(String(e.message || e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (err) {
    return (
      <div className="v1-wrap">
        <div className="v1-banner bad"><AlertTriangle size={16} /> VOL01 backend unreachable: {err}</div>
      </div>
    );
  }
  if (!state) return <div className="v1-wrap v1-dim">Loading VOL01 state…</div>;

  const ruleChanges = state.unacked_rule_changes || [];
  return (
    <div className="v1-wrap">
      <div className="v1-header">
        <h2><Activity size={20} /> VOL01 — Variance Harvest</h2>
        <div className="v1-header-meta">
          <span>pipeline <FreshBadge level={state.transport_freshness} /></span>
          <span className="v1-dim">rule {state.rule_hash || '—'}</span>
          {loadedAt && <span className="v1-dim">ui {loadedAt.toLocaleTimeString()}</span>}
        </div>
      </div>

      {state.error && <div className="v1-banner warn">backend: {state.error} (retrying)</div>}
      {state.schema_ahead && (
        <div className="v1-banner warn"><AlertTriangle size={15} />
          Pipeline contract is AHEAD of this app — update the dashboard before trusting details.
        </div>
      )}
      {ruleChanges.length > 0 && (
        <div className="v1-banner bad"><ShieldAlert size={15} />
          VALIDATED RULE CHANGED on {ruleChanges[0].first_seen?.slice(0, 10)} ({ruleChanges[0].old_hash} → {ruleChanges[0].new_hash}).
          Displayed history may not be comparable. Verify against DECISIONS.md before acting.
        </div>
      )}
      {(state.quarantine_count || 0) > 0 && (
        <div className="v1-banner bad"><ShieldAlert size={15} />
          BOUNDARY BREACH: {state.quarantine_count} unvalidated-asset row(s) quarantined
          (gold/crude are ignore-ruled). Investigate the pipeline.
        </div>
      )}

      <div className="v1-cards">
        {ASSETS.map((a) => (
          <AssetCard key={a} asset={a} row={state.assets?.[a]} history={hist[a]} />
        ))}
      </div>

      <div className="v1-section">
        <h4>Paper trail (verbatim from pipeline)</h4>
        <div className="v1-tablewrap">
          <table className="v1-table">
            <thead><tr><th>Date</th><th>Asset</th><th>IV</th><th>K0</th><th>HAR</th><th>Edge</th><th>State</th></tr></thead>
            <tbody>
              {ASSETS.flatMap((a) => (hist[a] || []).slice(-15).reverse().map((r) => (
                <tr key={`${a}-${r.date}`}>
                  <td>{r.date}</td><td>{r.asset}</td><td>{r.iv}</td><td>{r.k0}</td>
                  <td>{r.rv_fc_vol}</td><td className="v1-num">{r.edge}</td>
                  <td><span className={`v1-state sm ${r.state === 'DEPLOY' ? 'ok' : 'flat'}`}>{r.state}</span></td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="v1-section">
        <h4>Falsification conditions (D-063 — pre-committed)</h4>
        <ul className="v1-falsify">
          <li>(a) Any gated trade reaching the liquidation buffer → stand down (manual flag)</li>
          <li>(b) First 6+ live trades mean &lt; +3.6 vp → stand down (pipeline-computed)</li>
          <li>(c) Rolling λ &gt; 1.0 while gauge deploys → full stop (pipeline-computed)</li>
          <li>(d) Venue friction &gt; ~3 vp → stand down (manual, from live quotes)</li>
          <li>(e) App/pipeline divergence &gt; 25% of days → SYSTEM KILL (reconcile job)</li>
          <li>(f) Ungated premium negative 12+ months → starvation review</li>
        </ul>
      </div>
    </div>
  );
}
